import { DownloadOutlined, PrinterOutlined } from "@ant-design/icons";
import { Affix, Button, Card, Form, Space, message } from "antd";
import { BGMEA_UD_COST_LIST_ENDPOINT } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import HandleNotification from "common/Notification";
import { isMobileView } from "components/Common/AppHelper";
import Header from "components/Common/Header";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BgmeaUdCostForm from "../BgmeaUdCostForm";

const UpdateBgmeaUdCost = (props) => {
  const { width, viewMode } = props;
  const [editInfo, setEditInfo] = useState([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { bgmeaUdCostId } = useParams();

  const getBgmeaUdCostInfo = useCallback(
    async (id) => {
      const query = `${BGMEA_UD_COST_LIST_ENDPOINT}/${id}`;
      const response = await getData(query);

      if (response && response?.data?.code === 200) {
        const bgmeaUdCostData = response?.data?.data;
        console.log("API Response for edit:", response?.data);
        console.log("BGMEA UD Cost Data:", bgmeaUdCostData);
        console.log(
          "Available fields in API data:",
          Object.keys(bgmeaUdCostData || {}),
        );

        form.setFieldsValue({
          name: bgmeaUdCostData?.name,
          description: bgmeaUdCostData?.description,
        });
        setEditInfo(bgmeaUdCostData);
      } else {
        HandleNotification(
          "error",
          "Error!",
          response?.message || "Failed to get BGMEA UD Cost data",
        );
      }
    },
    [form],
  );

  useEffect(() => {
    if (bgmeaUdCostId) {
      getBgmeaUdCostInfo(bgmeaUdCostId);
    }
  }, [bgmeaUdCostId, getBgmeaUdCostInfo]);

  const discardForm = () => {
    navigate("/import-export-expense-tracking/bgmea-ud-cost");
  };

  const updateBgmeaUdCost = () => {
    form.submit();
  };

  const getFileById = async (id) => {
    try {
      const query = `/api/exim-cost/bgmea-ud-cost/pdf/${id}`;
      // Use 'arraybuffer' responseType to handle raw binary data
      const response = await getData(query, false, null, null, "arraybuffer");

      if (response && response.status === 200) {
        // Convert raw binary data to blob
        const blob = new Blob([response.data], { type: "application/pdf" });
        return URL.createObjectURL(blob);
      }
      return null;
    } catch (error) {
      console.error("Error fetching file:", error);
      return null;
    }
  };

  const handleDownload = () => {
    getFileById(bgmeaUdCostId)
      .then((blobUrl) => {
        if (blobUrl) {
          try {
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `bgmea-ud-cost-voucher.pdf`;
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the blob URL after download starts
            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

            message.success({
              content: "Download started successfully",
              key: "download",
              duration: 2,
            });
          } catch (error) {
            console.error("Download error:", error);
            message.error({
              content: "Error starting download",
              key: "download",
            });
          }
        } else {
          message.error({
            content: "No file available for download",
            key: "download",
          });
        }
      })
      .catch((error) => {
        console.error("Download error:", error);
        message.error({
          content: "Failed to download file",
          key: "download",
        });
      });
  };

  const handlePrint = () => {
    getFileById(bgmeaUdCostId)
      .then((blobUrl) => {
        if (blobUrl) {
          try {
            // Create a hidden iframe to load the PDF
            const iframe = document.createElement("iframe");
            iframe.style.position = "fixed";
            iframe.style.right = "0";
            iframe.style.bottom = "0";
            iframe.style.width = "0";
            iframe.style.height = "0";
            iframe.style.border = "0";
            iframe.src = blobUrl;

            // Add the iframe to the document
            document.body.appendChild(iframe);

            // Wait for iframe to load, then print
            iframe.onload = () => {
              try {
                // Focus the iframe
                iframe.contentWindow.focus();

                // Start print directly using browser's print function
                setTimeout(() => {
                  iframe.contentWindow.print();

                  message.success({
                    content: "Print dialog opened",
                    key: "print",
                    duration: 3,
                  });

                  // Keep the iframe in the DOM so it doesn't close
                  // No auto-close or cleanup to allow user to complete printing
                }, 1000);
              } catch (printError) {
                console.error("Print dialog error:", printError);

                // Fallback to opening in new window if direct print fails
                const printWindow = window.open(blobUrl, "_blank");
                if (printWindow) {
                  message.info({
                    content:
                      "PDF opened in new tab. Please use browser print function.",
                    key: "print",
                    duration: 5,
                  });
                  setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
                } else {
                  message.error({
                    content:
                      "Unable to open print dialog. Check popup settings.",
                    key: "print",
                  });
                  URL.revokeObjectURL(blobUrl);
                }

                // Remove the iframe since direct print failed
                document.body.removeChild(iframe);
              }
            };

            // Handle iframe load errors
            iframe.onerror = () => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(blobUrl);
              message.error({
                content: "Failed to load PDF for printing",
                key: "print",
              });
            };
          } catch (error) {
            console.error("Print error:", error);
            message.error({
              content: "Print functionality is not available",
              key: "print",
            });
            URL.revokeObjectURL(blobUrl);
          }
        } else {
          message.error({
            content: "No file available for printing",
            key: "print",
          });
        }
      })
      .catch((error) => {
        console.error("Print error:", error);
        message.error({
          content: "Failed to retrieve document for printing",
          key: "print",
        });
      });
  };

  const ActionUI = () => {
    return (
      <Space>
        {viewMode && bgmeaUdCostId && (
          <>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              type="default"
            >
              Download
            </Button>
            <Button
              size="small"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              type="default"
            >
              Print
            </Button>
          </>
        )}

        <Button size="small" key={1} danger onClick={() => discardForm()}>
          {viewMode ? "Back" : "Discard"}
        </Button>

        <Button
          size="small"
          key={2}
          type="primary"
          onClick={() => updateBgmeaUdCost()}
          hidden={viewMode}
        >
          Update BGMEA UD Cost
        </Button>
      </Space>
    );
  };

  return (
    <>
      <Affix offsetTop={0}>
        <Card
          className="p-fixed"
          style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}
        >
          <Header
            title={`${viewMode ? "View" : "Edit"} BGMEA UD Cost`}
            subTitle=""
            addLink="/"
            addText="Add New"
            actions={<ActionUI />}
          />
        </Card>
      </Affix>

      <BgmeaUdCostForm
        form={form}
        isEdit={true}
        viewMode={viewMode}
        editInfo={editInfo}
      />
    </>
  );
};

export default UpdateBgmeaUdCost;
